// Capacity vs. planned load (Workload Intelligence Phase 2). Two rules the
// components enforce so no caller can slip: a PERCENTAGE appears only for a
// person whose capacity is configured (`capacity` non-null) — everyone else
// reads "Capacity not set"; and the copy describes the PLAN ("Over capacity"
// = the planned work exceeds the configured capacity), never the person.
// Units are relative effort units per week (the effort-weight scale), never
// hours — the labels say "units".
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkloadCapacityView } from '@/services/workloadService';

type T = (key: string, opts?: Record<string, unknown>) => string;

/** Bar width: utilization clamped to the track (≥ 100% fills it). */
export const utilizationWidth = (pct: number): number => Math.max(2, Math.min(100, Math.round(pct)));

/** Colour describes the plan against capacity: comfortable / near / over. */
export const utilizationBarClass = (c: WorkloadCapacityView): string => (
  c.over_capacity ? 'bg-danger' : c.utilization_pct >= 85 ? 'bg-warning' : 'bg-success'
);

export const formatUnits = (t: T, n: number): string =>
  t('capacity_units_value', { defaultValue: '{{count}} units', count: Number.isInteger(n) ? n : Math.round(n * 10) / 10 });

/** Compact table cell: thin bar + "83%" (+ "Over capacity" when the plan exceeds
 * capacity). Not configured → the muted text "Capacity not set". */
export const CapacityCell: React.FC<{ capacity: WorkloadCapacityView | null | undefined; testId?: string }> = ({ capacity, testId }) => {
  const { t } = useTranslation('work');
  if (!capacity) {
    return <span className="text-xs text-muted-foreground" data-testid={testId ? `${testId}-unset` : undefined}>{t('capacity_not_set', { defaultValue: 'Capacity not set' })}</span>;
  }
  const pct = Math.round(capacity.utilization_pct);
  const title = t('capacity_cell_title', {
    defaultValue: '{{load}} of {{units}} units planned · {{headroom}} units headroom',
    load: capacity.load_units, units: capacity.units, headroom: capacity.headroom_units,
  });
  return (
    <div className="min-w-[120px]" data-testid={testId} title={title}>
      <div className="flex items-center gap-2 text-xs">
        <span className={`tabular-nums ${capacity.over_capacity ? 'text-danger-strong font-medium' : 'text-foreground'}`}>{pct}%</span>
        {capacity.over_capacity && (
          <span className="text-[11px] text-danger-strong" data-testid={testId ? `${testId}-over` : undefined}>
            {t('capacity_over', { defaultValue: 'Over capacity' })}
          </span>
        )}
      </div>
      <div className="mt-1 h-1 w-full rounded-full bg-muted" aria-hidden="true">
        <div className={`h-1 rounded-full ${utilizationBarClass(capacity)}`} style={{ width: `${utilizationWidth(capacity.utilization_pct)}%` }} />
      </div>
    </div>
  );
};

/** Drill-down / My Workload block: units, load, headroom, utilization. */
export const CapacityBlock: React.FC<{ capacity: WorkloadCapacityView | null | undefined; className?: string }> = ({ capacity, className = '' }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as T;
  return (
    <section className={`rounded-[10px] border border-border p-3 ${className}`} data-testid="capacity-block" aria-label={t('capacity', { defaultValue: 'Capacity' }) as string}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-medium text-charcoal">{t('capacity', { defaultValue: 'Capacity' })}</h4>
        {capacity?.over_capacity && (
          <span className="text-[11px] text-danger-strong" data-testid="capacity-block-over">{t('capacity_over', { defaultValue: 'Over capacity' })}</span>
        )}
      </div>
      {!capacity ? (
        <p className="mt-1 text-xs text-muted-foreground" data-testid="capacity-block-unset">{t('capacity_not_set', { defaultValue: 'Capacity not set' })}</p>
      ) : (
        <>
          <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">{t('capacity_units_label', { defaultValue: 'Capacity' })}</dt>
              <dd className="tabular-nums text-foreground" data-testid="capacity-units">{formatUnits(tt, capacity.units)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('capacity_load', { defaultValue: 'Planned load' })}</dt>
              <dd className="tabular-nums text-foreground" data-testid="capacity-load">{formatUnits(tt, capacity.load_units)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('capacity_headroom', { defaultValue: 'Headroom' })}</dt>
              <dd className={`tabular-nums ${capacity.headroom_units < 0 ? 'text-danger-strong' : 'text-foreground'}`} data-testid="capacity-headroom">{formatUnits(tt, capacity.headroom_units)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('capacity_utilization', { defaultValue: 'Utilization' })}</dt>
              <dd className={`tabular-nums ${capacity.over_capacity ? 'text-danger-strong font-medium' : 'text-foreground'}`} data-testid="capacity-utilization">{Math.round(capacity.utilization_pct)}%</dd>
            </div>
          </dl>
          <div className="mt-2 h-1.5 w-full rounded-full bg-muted" aria-hidden="true">
            <div className={`h-1.5 rounded-full ${utilizationBarClass(capacity)}`} style={{ width: `${utilizationWidth(capacity.utilization_pct)}%` }} />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">{t('capacity_units_note', { defaultValue: 'Relative effort units per week — not hours.' })}</p>
        </>
      )}
    </section>
  );
};
